import { Alert, Avatar, Badge, Button, Group, Menu, Text, UnstyledButton, useComputedColorScheme, useMantineColorScheme } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle, FiCheck, FiChevronDown, FiEye, FiLogOut, FiMoon, FiSun, FiUser } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { Dyia } from '@web/features/assistant/Dyia';
import { useStopImpersonation } from '@web/features/auth/api';
import { usePlatformOrgs } from '@web/features/platform/api';
import { landingPath, setLastOrgId, useAuth } from '@web/lib/auth';
import { applyBranding } from '@web/lib/branding';
import { useFileUrl } from '@web/lib/files';
import { notifyError } from '@web/lib/errors';
import { useActiveOrgId, useOrgContext } from '@web/lib/org';
import { NAV_ITEMS, type NavItem } from '../navigation';
import { AppLayout } from './AppLayout';
import { ApiStatus } from './ApiStatus';
import { MyWells } from './MyWells';
import classes from './MainShell.module.css';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')).toUpperCase() || '?';
}

/** Org switcher: memberships for regular users, every organization for platform staff. */
function OrgSwitcher({ orgId, orgName }: { orgId?: string; orgName?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useAuth((s) => s.me);
  const isStaff = !!me?.user.platformRole;
  const platformOrgs = usePlatformOrgs(isStaff);
  const options = isStaff
    ? (platformOrgs.data ?? []).map((o) => ({ id: o.id, name: o.name, suspended: o.status === 'suspended' }))
    : (me?.memberships ?? []).map((m) => ({ id: m.orgId, name: m.orgName, suspended: m.orgStatus === 'suspended' }));

  if (!isStaff && options.length <= 1) {
    return orgName ? (
      <Text fw={600} size="sm" c="var(--tl-text)" truncate>
        {orgName}
      </Text>
    ) : null;
  }
  return (
    <Menu position="bottom-start" width={260}>
      <Menu.Target>
        <UnstyledButton className={classes.orgButton} aria-label={t('shell.switchOrg')}>
          <span className="tl-caption">{t('shell.organization')}</span>
          <Group gap={6} wrap="nowrap">
            <Text fw={600} size="sm" c="var(--tl-text)" truncate maw={220}>
              {orgName ?? t('shell.selectOrg')}
            </Text>
            <FiChevronDown size={14} aria-hidden />
          </Group>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown mah={360} style={{ overflowY: 'auto' }}>
        <Menu.Label>{t('shell.organization')}</Menu.Label>
        {options.length === 0 && <Menu.Item disabled>{t('shell.noOrgs')}</Menu.Item>}
        {options.map((o) => (
          <Menu.Item
            key={o.id}
            rightSection={o.id === orgId ? <FiCheck size={14} /> : o.suspended ? <Badge color="orange" size="xs">{t('status.suspended')}</Badge> : null}
            onClick={() => {
              setLastOrgId(o.id);
              navigate(`/o/${o.id}`);
            }}
          >
            {o.name}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function UserMenu() {
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, signOut } = useAuth();
  if (!me) return null;
  const name = me.user.name || me.user.email;
  return (
    <Menu position="bottom-end" width={260}>
      <Menu.Target>
        <UnstyledButton className={classes.userButton} aria-label={t('shell.userMenu')}>
          <Avatar size={34} radius="xl" color="brand">
            {initials(name)}
          </Avatar>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <div className={classes.userHead}>
          <Text fw={600} size="sm" c="var(--tl-text)" truncate>
            {name}
          </Text>
          <Text size="xs" c="dimmed" truncate>
            {me.user.email}
          </Text>
          {me.user.platformRole && (
            <Badge mt={6} color="brand">
              {t(`roles.${me.user.platformRole}`)}
            </Badge>
          )}
        </div>
        <Menu.Divider />
        <Menu.Item leftSection={<FiUser size={16} />} onClick={() => navigate('/profile')}>
          {t('shell.profile')}
        </Menu.Item>
        <Menu.Item leftSection={dark ? <FiSun size={16} /> : <FiMoon size={16} />} onClick={() => setColorScheme(dark ? 'light' : 'dark')}>
          {t(dark ? 'shell.lightTheme' : 'shell.darkTheme')}
        </Menu.Item>
        <Menu.Item
          leftSection={<FiLogOut size={16} />}
          onClick={() => {
            void signOut().then(() => navigate('/login', { replace: true }));
          }}
        >
          {t('shell.signOut')}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/** Red banner shown for the whole "view as" session (doc 02 §9, doc 09 §2). */
function ImpersonationBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me, reload } = useAuth();
  const stop = useStopImpersonation();
  if (!me?.impersonation) return null;
  return (
    <div className={classes.impersonation} role="alert">
      <FiEye size={16} aria-hidden />
      <span>{t('shell.viewingAs', { name: me.impersonation.targetName })}</span>
      <Button
        size="compact-xs"
        variant="white"
        color="red"
        loading={stop.isPending}
        onClick={() =>
          stop.mutate(undefined, {
            onSuccess: async () => {
              qc.clear();
              const fresh = await reload();
              if (fresh) navigate(landingPath(fresh), { replace: true });
            },
            onError: (e) => notifyError(e),
          })
        }
      >
        {t('shell.stopViewingAs')}
      </Button>
    </div>
  );
}

export function MainShell() {
  const { t } = useTranslation();
  const me = useAuth((s) => s.me);
  const orgId = useActiveOrgId();
  const org = useOrgContext(orgId);
  const permissions = new Set(org.data?.permissions ?? []);
  const platformPermissions = new Set(me?.platformPermissions ?? []);
  const isClient = org.data?.role === 'client';

  useEffect(() => {
    if (org.data) setLastOrgId(org.data.id);
  }, [org.data]);

  // Organization branding (doc 02 §10): its colour and logo while working inside it.
  const dark = useComputedColorScheme('light') === 'dark';
  const primary = org.data?.branding.primaryColor;
  useEffect(() => {
    applyBranding(primary, dark);
    return () => applyBranding(null, dark);
  }, [primary, dark]);
  const logoUrl = useFileUrl(org.data?.id, org.data?.branding.logoFileId);

  const items = NAV_ITEMS.filter((item) => {
    if (item.scope === 'platform') return item.anyOf.some((p) => platformPermissions.has(p));
    if (!org.data) return false;
    if (item.hideForClient && isClient) return false;
    return item.anyOf.length === 0 || item.anyOf.some((p) => permissions.has(p));
  });
  const hrefFor = (item: NavItem) => (item.scope === 'platform' ? item.path : `/o/${orgId}${item.path ? `/${item.path}` : ''}`);
  const homeHref = org.data ? `/o/${orgId}` : me?.user.platformRole ? '/platform/orgs' : '/';

  const banners = (
    <>
      <ImpersonationBanner />
      {org.data?.status === 'suspended' && (
        <Alert color="orange" icon={<FiAlertTriangle />} radius={0} role="status">
          {t('shell.orgSuspended')}
        </Alert>
      )}
    </>
  );

  return (
    <AppLayout
      items={items}
      hrefFor={hrefFor}
      homeHref={homeHref}
      banners={banners}
      brand={org.data ? { name: org.data.name, logoUrl: logoUrl ?? null } : undefined}
      navExtra={org.data && !isClient && permissions.has('assets.view') ? <MyWells orgId={org.data.id} /> : null}
      headerStart={
        <>
          <OrgSwitcher orgId={org.data?.id} orgName={org.data?.name} />
        </>
      }
      headerEnd={
        <>
          {org.data?.assistant && <Dyia orgId={org.data.id} />}
          <ApiStatus />
          <UserMenu />
        </>
      }
    />
  );
}

/** Link used by pages that need "go to the org home". */
export function OrgHomeLink({ children }: { children: string }) {
  const orgId = useActiveOrgId();
  return <Link to={orgId ? `/o/${orgId}` : '/'}>{children}</Link>;
}
